import $                        from "jquery";
import tippy                    from "tippy.js";
import Alpine                   from "alpinejs";
import Helpers                  from "../utilities/helpers";
import initJQueryScrollTo       from "../jsvendor/jquery.scrollTo";
import initDebounce             from "../utilities/debounce";
import ScrollTrigger            from "gsap/ScrollTrigger";
import { initDictionaryModalsAudio } from "./dictionary-modals";

var isScrolling = false;
var isInLoadingState = false;

/**
 * Initialises the tippy.js's that display in the article contents.
 */
export function DictionaryTippys() { 
    initDebounce( $ );

    $( initArticleReplacements );
}

/**
 * Initialise the article / blog post replacements.
 */
function initArticleReplacements() { 
    // On click to the modal, debounce the actual load event so it only happens once and add the `loading` class.
    $( ".single.single-post .dictionary-term.replaced" )
        .on( "click", $.debounce( 150, onClickReplacedDictionaryTerm ) )
        .on( "click", function () { 
            $( this ).addClass( "loading" );
        } ) ;
}

/**
 * Called on click to any dictionary term which has been replaced.
 */
function onClickReplacedDictionaryTerm() { 
    const element = this;

    // Stop the user from clicking a bunch of them and breaking the page for themselves.
    if ( isInLoadingState ) {
        $( element ).removeClass( "loading" );
        return;
    }
    isInLoadingState = true;

    // Download the dictionary modal
    downloadDictionaryModal( this.dataset[ "termId" ] ).then( results => { 
        if ( ! results.success ) {
            return;
        }

        // If HTML is passed back then display it.
        if ( results.html ) {
            $( "body" ).append( results.html );

            // Audio clips need the handlers loading.
            initDictionaryModalsAudio();
        }

        // Remove the loading state from the link.
        $( element ).removeClass( "loading" );
        isInLoadingState = false;
    } ).catch( error => { 
        console.error( error );

        // Remove the loading state from the link.
        $( element ).removeClass( "loading" );
        isInLoadingState = false;
    } );
}

/**
 * Downloads the necessary modal to show a dictionary term on click, instead of tippy.js.
 * 
 * @param {Number} id
 * @return {Promise<{}>}
 */
async function downloadDictionaryModal( id ) { 
    return $.ajax( {
        url: `${ window.location.origin }/wp-admin/admin-ajax.php`,
        method: "POST",
        dataType: "JSON",
        data: { 
            action: "ifg_download_term_information",
            term_id: id
        }
    } );
}

/**
 * Initialises the dictionary page functionality.
 */
export function DictionaryArchive() { 
    initJQueryScrollTo( $ );
    initJQueryScrollTo( jQuery );

    initDebounce( $ );
    initDebounce( jQuery );

    $( initDictionary );
}

/**
 * Decodes a base64 encoded string for the tippy popups.
 * 
 * @param {HTMLElement} element
 * @return {String}
 */
function tippyContentDecode( element ) {
    const content = element.dataset[ "tippyTermDefinition" ];

    // Return empty string if the element contains invalid dataset.
    if ( ( ! content ) || ( ! content.length ) ) {
        return "";
    }

    try {
        const decoded = atob( content );

        // Debug output for some tracing.
        if ( Helpers.isDebug() ) {
            console.info( `Decoded term "${ element.textContent }" with definition: "${ decoded }"` );
        }

        return decoded;
    } catch ( _ ) { }

    return "";
}

/**
 * Initialises the archive dictionary functionality.
 */
function initDictionary() {
    // Set the range slider max value to the number of letters we have (minus 1, zero-indexed array).
    proxy().max = availableLetters().length - 1;
    proxy().min = 0; // And force the min value to zero.

    // Bind the change event on the slider.
    $( "#current-letter-index" ).on( "change", $.debounce( 150, onLetterChanged ) );

    // Calculate the height at which we need to change letter.
    const targetHeight = ( $( "#archive-dictionary-slider" ).outerHeight() + $( "#archive-dictionary-slider" ).height() ).toFixed( 0 );

    // Iterate on each of the letters and define the scroll trigger logic.
    availableLetters().forEach( letter => {
        // Generate the expected ID of the element for the scroll triggers.
        const id = `#dictionary-term-${ letter.letter }`;

        // Define wrapper function to set the current letter by ID, using the current ID.
        var fnSetCurrentLetter = () => {
            setCurrentLetter( letterByContainerId( id ) );
        };

        // Locate the trigger element by generated ID.
        const triggerElement = document.querySelector( id );
        if ( ! triggerElement ) {
            return;
        }

        // Base scroll trigger definition, everything else is filled in.
        var scrollTriggerConfig = {
            start:          `top ${ targetHeight }px`,
            end:            `bottom ${ targetHeight }px`,
            onEnter:        fnSetCurrentLetter,
            onEnterBack:    fnSetCurrentLetter,
            trigger:        triggerElement,
        };

        // If we're debugging then show the markers debug.
        if ( Helpers.isDebug() ) {
            scrollTriggerConfig[ 'markers' ] = true;
        }

        // Watch the elements scroll.
        ScrollTrigger.create( scrollTriggerConfig );
    } );
}

/**
 * Sets the current letter, which sets the range slider.
 * 
 * @param {string} letter 
 */
function setCurrentLetter( letter ) {
    if ( isScrolling ) {
        return;
    }

    var letterIndex = -1;

    // Scan to find the letter index.
    availableLetters().forEach( ( value, index ) => {
        if ( value.letter === letter ) {
            letterIndex = index;
        }
    })    

    // If the index was found then update it.
    if ( letterIndex !== -1 ) {
        proxy().index = letterIndex;
    }
}
 
/**
 * Converts a container element ID into a letter.
 * 
 * @param {String} containerId 
 * @return {JQuery}
 */
function letterByContainerId( containerId ) {
    const $elem = $( containerId );

    return $elem.data( "letter" );
}

/**
 * Called when the slider is moved and the selected letter is updated.
 */
function onLetterChanged() { 
    // Get the new index and the letter it's mapped to.
    const index         = proxy().index ?? 0;
    const letterData    = availableLetters()[ index ];

    if ( ! letterData ) {
        return;
    }

    // Find the target element.
    const $elem = $( `[data-letter="${ letterData.letter }"]` );
    if ( ! $elem.length ) {
        return;
    }

    // Calculate the target height. Guesses are being made here due to padding etc.
    const targetHeight = $elem.offset().top - ( $( "#archive-dictionary-slider" ).outerHeight() + $( "#archive-dictionary-slider" ).height() );

    $( window.document.body ).scrollTo( targetHeight, 150 );

    isScrolling = true;
    
    setTimeout( function () { 
        isScrolling = false;
    }, 150 );
}

/**
 * Gets the list of available letters we can scan for in the database.
 * 
 * @returns {null|{letter:string,frequency:number}[]}
 */
function availableLetters() { 
    return Helpers.vlConfig( "dictionary_letters" );
}

/**
 * Gets the proxy to the data for the component from Alpine.
 * 
 * @returns {Proxy<{min:number,max:number,index:number}>}
 */
function proxy() { 
    return Alpine.$data( $( "#archive-dictionary-slider" ).get( 0 ) );
}